import { useCallback, useState, useMemo } from 'preact/hooks'
import { AllHintStepData, HintStep } from '../types'

export type KeywordSubmitError = 'NO_MATCH' | 'ALREADY_USED'

export const useStepsManager = (allSteps: AllHintStepData) => {
  const initialKeys = Object.entries(allSteps).flatMap(([key, step]) =>
    step.keywords === undefined || step.keywords.length === 0 ? [key] : []
  )

  const [visibleStepKeys, setVisibleStepKeys] = useState<string[]>(initialKeys)
  const [currentStepKey, setCurrentStepKey] = useState<string | undefined>(
    initialKeys[0]
  )

  const [keywordSubmitError, setKeywordSubmitError] = useState<
    KeywordSubmitError | undefined
  >(undefined)

  const stepIndex: Record<string, number> = useMemo(() => {
    return Object.fromEntries(Object.keys(allSteps).map((k, i) => [k, i]))
  }, [allSteps])

  const submitKeyword = useCallback(
    (val: string, options?: { suppressError?: boolean }) => {
      const foundStep = Object.entries(allSteps).find(
        ([, s]) => s.keywords !== undefined && s.keywords.includes(val)
      )

      const suppressError = options?.suppressError ?? false

      if (!foundStep) {
        if (!suppressError) {
          setKeywordSubmitError('NO_MATCH')
        }
        return false
      }
      const [foundKey] = foundStep

      setVisibleStepKeys((prev) => {
        if (prev.includes(foundKey)) {
          if (!suppressError) {
            setKeywordSubmitError('ALREADY_USED')
          }
          return prev
        }

        setCurrentStepKey(foundKey)
        setKeywordSubmitError(undefined)

        return [...prev, foundKey].sort((a, b) => stepIndex[a] - stepIndex[b])
      })

      return true
    },
    [allSteps, stepIndex]
  )

  const resetSubmitError = useCallback(() => {
    setKeywordSubmitError(undefined)
  }, [])

  const currentStep: HintStep | undefined = useMemo(() => {
    if (!currentStepKey) return undefined
    return allSteps[currentStepKey]
  }, [allSteps, currentStepKey])

  const visibleButtons: { key: string; label: string }[] = useMemo(() => {
    return visibleStepKeys.flatMap((k) => {
      const step = allSteps[k]
      if (!step) return []
      return { key: k, label: step.buttonTitle }
    })
  }, [allSteps, visibleStepKeys])

  return {
    currentStep,
    visibleButtons,
    submitKeyword,
    keywordSubmitError,
    currentStepKey,
    setCurrentStepKey,
    resetSubmitError,
  } as const
}
